/**
 * useGlobalAudioAnalyser Hook
 * 
 * Captures real-time audio data from the global Fr00t audio system.
 * Connects an analyser node to the main master gain to visualize all audio output.
 */

import { useState, useEffect, useRef } from 'react';
import { audioManager } from '../core/audio/AudioManagerAdapter';

interface GlobalAudioData {
  frequencyData: Uint8Array;
  averageAmplitude: number;
}

export function useGlobalAudioAnalyser() {
  const [audioData, setAudioData] = useState<GlobalAudioData | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    let unsubscribeFn: (() => void) | null = null;

    const setupAnalyser = async () => {
      // Wait for AudioManager to be ready
      if (!audioManager.isReady()) {
        unsubscribeFn = audioManager.onInitialized(() => {
          if (mounted) {
            setupAnalyser();
          }
        });
        return;
      }

      const audioContext = audioManager.getMainAudioContext();
      const masterGain = audioManager.getMainMasterGain();

      if (!audioContext || !masterGain) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Global audio analyser: Audio context or master gain not available');
        }
        return;
      }

      // Create analyser node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048; // Higher resolution for better frequency analysis
      analyser.smoothingTimeConstant = 0.8; // Smooth transitions
      analyserRef.current = analyser;

      // Connect analyser to master gain output
      // In Web Audio API, you can connect multiple nodes from one source
      // This won't break the existing connection to destination
      masterGain.connect(analyser);

      // Start analysis loop with frame throttling for performance (30fps)
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      let frameCount = 0;
      const throttleFrames = 2; // Update React state every 2 frames (30fps instead of 60fps)
      const targetFPS = 30;
      const frameInterval = 1000 / targetFPS; // ~33.33ms per frame
      let lastUpdateTime = 0;

      const analyze = () => {
        if (!mounted || !analyserRef.current) {
          return;
        }

        const now = performance.now();
        analyser.getByteFrequencyData(dataArray);

        // Calculate average amplitude for overall visualization intensity
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const averageAmplitude = sum / bufferLength;

        // Check if audio is playing (has significant amplitude)
        const hasAudio = averageAmplitude > 2; // Lower threshold for better responsiveness

        // Throttle React state updates to 30fps to prevent excessive re-renders
        // Only update state if enough time has passed (target 30fps)
        if (mounted && (now - lastUpdateTime >= frameInterval)) {
          lastUpdateTime = now;
          // Create a COPY of the array so React detects the change
          const frequencyDataCopy = new Uint8Array(dataArray);
          setAudioData({
            frequencyData: frequencyDataCopy,
            averageAmplitude
          });
          setIsPlaying(hasAudio);
        }

        animationFrameRef.current = requestAnimationFrame(analyze);
      };

      analyze();
    };

    setupAnalyser();

    return () => {
      mounted = false;
      // Unsubscribe from audio manager if subscription exists
      if (unsubscribeFn) {
        unsubscribeFn();
        unsubscribeFn = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
    };
  }, []);

  return { audioData, isPlaying };
}

